import React, { useState, useMemo } from 'react';
import { FormData } from '../types';
import { PREDEFINED_TAGS } from '../../../../pages/DesignSystem/types/tags.types';
import { formatEventDateTime, formatEventDateForIcon } from '../../../../utils/timezoneUtils';
import { SecurityUtils } from '../../../../utils/security';
import { useSmartSeries } from '../../../../hooks/useSmartSeries';
import {
  Icon,
  DateIcon,
  LogoSVG,
  Button,
  ShareButton,
  FavoriteButton,
  NotificationBubble,
  Tag,
  GlassPanel
} from '../../../../pages/DesignSystem';
import '../../../../components/event/card.css';
import '../../../../components/event/RecurringEventCard.css';
import './PreviewStep.css';

interface PreviewStepProps {
  formData: FormData;
}

const PreviewStep: React.FC<PreviewStepProps> = ({ formData }) => {
  const [showShareNotification, setShowShareNotification] = useState(false);
  const [showDates, setShowDates] = useState(false);

  const getEndDate = () => {
    if (!formData.isRecurring || !formData.startDate) return undefined;

    if (formData.endType === 'date' && formData.endDate) {
      return formData.endDate;
    }

    if (formData.endType === 'occurrences' && formData.occurrences) {
      const startDate = new Date(formData.startDate);
      const weeksNeeded = Math.ceil(formData.occurrences / (formData.weekDays?.length || 1));
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + (weeksNeeded * 7));
      return endDate.toISOString();
    }

    return undefined;
  };

  const previewSeries = formData.isRecurring ? {
    id: 'preview',
    organizerId: 'preview',
    timezone: formData.timezone,
    title: formData.title,
    status: 'draft' as const,
    createdAt: new Date(),
    updatedAt: new Date(),
    frequency: formData.frequency,
    weekDays: formData.weekDays || [],
    startDate: formData.startDate,
    endDate: getEndDate(),
    endType: formData.endType,
    occurrences: formData.endType === 'occurrences' ? formData.occurrences : undefined,
    time: formData.time || formData.startTime,
    endTime: formData.endTime
  } : undefined;

  const { pattern, loading: patternLoading } = useSmartSeries(undefined, previewSeries);

  const getDateTime = () => {
    if (formData.isRecurring) {
      return formData.startDate ? new Date(formData.startDate) : undefined;
    }
    return formData.date ? new Date(formData.date) : undefined;
  };

  const datetime = getDateTime();
  const hasImage = Boolean(formData.image);

  // Simplifier le pattern de récurrence
  const simplifiedPattern = useMemo(() => {
    if (!pattern) return 'Recurring';

    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const mentionedDays = allDays.filter(day =>
      new RegExp(`\\b${day}\\b`, 'i').test(pattern)
    );

    const untilMatch = pattern.match(/\s*(until\s+.+)$/i);
    const untilPart = untilMatch ? ` ${untilMatch[1]}` : '';

    if (mentionedDays.length >= 5) {
      const missingDays = allDays.filter(day => !mentionedDays.includes(day));
      if (missingDays.length === 0) {
        return `Every day${untilPart}`;
      } else if (missingDays.length === 1) {
        return `Every day except ${missingDays[0]}${untilPart}`;
      } else {
        const lastDay = missingDays.pop();
        return `Every day except ${missingDays.join(', ')} and ${lastDay}${untilPart}`;
      }
    }

    let simplified = pattern.replace(/^Every\s+/i, '');
    simplified = simplified.replace(/\s*[-–]\s*\d{1,2}:\d{2}/gi, '');
    simplified = simplified.replace(/\s+at\s+\d{1,2}:\d{2}(\s*(AM|PM))?/gi, '');
    simplified = simplified.replace(/\s+/g, ' ').trim();
    return simplified || 'Recurring';
  }, [pattern]);

  // Formater le pattern avec les jours en gras
  const formattedPattern = useMemo(() => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    let result = simplifiedPattern;

    days.forEach(day => {
      const regex = new RegExp(`\\b${day}\\b`, 'gi');
      result = result.replace(regex, `<strong>${day}</strong>`);
    });

    return result;
  }, [simplifiedPattern]);

  // Obtenir les heures
  const eventTime = formData.isRecurring
    ? `${formData.startTime || formData.time || ''}${formData.endTime ? ` - ${formData.endTime}` : ''}`
    : `${formData.time || ''}${formData.endTime ? ` - ${formData.endTime}` : ''}`;

  // Preview des dates pour les récurrents
  const previewSeriesEvents = useMemo(() => {
    if (!formData.isRecurring || !formData.startDate) return [];

    const events = [];
    const startDate = new Date(formData.startDate);

    if (isNaN(startDate.getTime())) return [];

    const endDate = formData.endDate ? new Date(formData.endDate) : undefined;

    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + (i * 7));

      if (endDate && date > endDate) break;

      events.push({
        id: `preview-${i}`,
        datetime: date,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }

    return events;
  }, [formData.isRecurring, formData.startDate, formData.endDate]);

  // Render tags
  const renderTags = () => (
    <div className="event-card__tags">
      {(formData.tags || []).map((tagId: string) => {
        const tag = PREDEFINED_TAGS.find(t => t.id === tagId);
        return tag ? (
          <Tag
            key={tagId}
            label={tag.label}
            type="activity"
            size="md"
          />
        ) : null;
      })}
    </div>
  );

  // Render header
  const renderHeader = () => (
    <div className="event-card__header">
      {renderTags()}
      <div className="event-card__actions" onClick={e => e.stopPropagation()}>
        <ShareButton
          shareData={{
            title: formData.title,
            text: `what2do.today\nCome discover ${formData.title}`,
            url: window.location.href
          }}
          variant="icon"
          size="lg"
          shouldTriggerSuccess={false}
          onShareSuccess={() => setShowShareNotification(true)}
        />
        <FavoriteButton
          id="preview"
          size="lg"
        />
      </div>
    </div>
  );

  // Render price
  const renderPriceText = () => {
    // No prices at all → show nothing
    if (!formData.prices || formData.prices.length === 0) {
      return null;
    }

    // Check if all prices have amount === 0 → Free
    const allFree = formData.prices.every(price => price.amount === 0);
    if (allFree) {
      return <span className="event-card__glass-price event-card__glass-price--free">Free</span>;
    }

    return (
      <span className="event-card__glass-price">
        {formData.prices.map((price, index) => (
          <React.Fragment key={index}>
            {index > 0 && ' · '}
            {price.amount === null
              ? (price.description || 'Contact organizer')
              : price.amount === 0
                ? 'Free'
                : `${price.amount}${price.currency === 'EUR' ? '€' : price.currency}`}
            {price.amount !== null && price.description && ` ${price.description}`}
          </React.Fragment>
        ))}
      </span>
    );
  };

  // Render liste des dates (récurrent)
  const renderRecurrencesList = () => (
    <div
      className={`recurring-card__dates-list ${showDates ? 'recurring-card__dates-list--expanded' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {previewSeriesEvents.map((event) => (
        <div key={event.id} className="recurring-card__date-item">
          <div className="recurring-card__date-info">
            <span className="recurring-card__date">
              {formatEventDateTime(event.datetime, event.timezone).date}
            </span>
            <span className="recurring-card__status-text recurring-card__status-text--upcoming">
              Preview
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  // Render card avec image
  const renderWithImage = () => (
    <div className={`event-card event-card--with-image event-card--revealed ${formData.isRecurring ? 'event-card--recurring' : ''}`}>
      {/* Image de fond */}
      <div className="event-card__background">
        <img
          src={formData.image}
          alt={formData.title}
          className="loaded"
        />
      </div>

      {/* Header avec tags et actions */}
      {renderHeader()}

      {/* Spacer pour récurrent */}
      {formData.isRecurring && <div className="event-card__spacer" />}

      {/* GlassPanel avec les infos */}
      <div className="event-card__glass-container">
        <GlassPanel variant="light" blur="md" radius="md">
          <div className="event-card__glass-content">
            {/* Titre */}
            <h3 className="event-card__glass-title">{formData.title}</h3>

            {/* Date/Time ou Récurrence */}
            {formData.isRecurring ? (
              <>
                <div className="event-card__glass-row">
                  <div className="event-card__glass-icon">
                    <Icon name="Repeat" size={20} />
                  </div>
                  <div className="event-card__glass-location-row">
                    <div className="event-card__glass-info">
                      <span
                        className="event-card__glass-secondary"
                        dangerouslySetInnerHTML={{ __html: SecurityUtils.sanitizeHtml(patternLoading ? 'Loading...' : formattedPattern) }}
                      />
                      <span className="event-card__glass-secondary">
                        {eventTime}
                      </span>
                    </div>
                    {previewSeriesEvents.length > 0 && (
                      <Button
                        variant="glass"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDates(!showDates);
                        }}
                        aria-label={showDates ? "Hide dates" : "Show dates"}
                      >
                        <Icon name={showDates ? "ChevronUp" : "ChevronDown"} size={16} />
                      </Button>
                    )}
                  </div>
                </div>
                {renderRecurrencesList()}
              </>
            ) : (
              datetime && (
                <div className="event-card__glass-row">
                  <div className="event-card__glass-icon">
                    <DateIcon
                      {...formatEventDateForIcon(datetime, formData.timezone)}
                      variant="dark"
                      size="sm"
                    />
                  </div>
                  <div className="event-card__glass-location-row">
                    <div className="event-card__glass-info">
                      <span className="event-card__glass-primary">
                        {formatEventDateTime(datetime, formData.timezone).weekday}
                      </span>
                      <span className="event-card__glass-secondary">
                        {eventTime}
                      </span>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Lieu */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="MapPin" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-location-text">
                  <span className="event-card__glass-location-name">
                    {formData.locationName || formData.location || 'Location not set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Prix */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="Ticket" size={20} />
              </div>
              <div className="event-card__glass-info event-card__glass-info--row">
                {renderPriceText()}
              </div>
            </div>
          </div>
        </GlassPanel>
      </div>
    </div>
  );

  // Render card sans image (fallback)
  const renderWithoutImage = () => (
    <div className={`event-card event-card--with-image event-card--fallback event-card--revealed ${formData.isRecurring ? 'event-card--recurring' : ''}`}>
      {/* Logo en fond */}
      <div className="event-card__background event-card__background--fallback">
        <LogoSVG
          variant="vertTexte"
          size="auto"
          padding={0}
        />
      </div>

      {/* Header avec tags et actions */}
      {renderHeader()}

      {/* Spacer pour récurrent */}
      {formData.isRecurring && <div className="event-card__spacer" />}

      {/* GlassPanel avec les infos */}
      <div className="event-card__glass-container">
        <GlassPanel variant="light" blur="md" radius="md">
          <div className="event-card__glass-content">
            {/* Titre */}
            <h3 className="event-card__glass-title">{formData.title}</h3>

            {/* Date/Time ou Récurrence */}
            {formData.isRecurring ? (
              <>
                <div className="event-card__glass-row">
                  <div className="event-card__glass-icon">
                    <Icon name="Repeat" size={20} />
                  </div>
                  <div className="event-card__glass-location-row">
                    <div className="event-card__glass-info">
                      <span
                        className="event-card__glass-secondary"
                        dangerouslySetInnerHTML={{ __html: SecurityUtils.sanitizeHtml(patternLoading ? 'Loading...' : formattedPattern) }}
                      />
                      <span className="event-card__glass-secondary">
                        {eventTime}
                      </span>
                    </div>
                    {previewSeriesEvents.length > 0 && (
                      <Button
                        variant="glass"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDates(!showDates);
                        }}
                        aria-label={showDates ? "Hide dates" : "Show dates"}
                      >
                        <Icon name={showDates ? "ChevronUp" : "ChevronDown"} size={16} />
                      </Button>
                    )}
                  </div>
                </div>
                {renderRecurrencesList()}
              </>
            ) : (
              datetime && (
                <div className="event-card__glass-row">
                  <div className="event-card__glass-icon">
                    <DateIcon
                      {...formatEventDateForIcon(datetime, formData.timezone)}
                      variant="dark"
                      size="sm"
                    />
                  </div>
                  <div className="event-card__glass-location-row">
                    <div className="event-card__glass-info">
                      <span className="event-card__glass-primary">
                        {formatEventDateTime(datetime, formData.timezone).weekday}
                      </span>
                      <span className="event-card__glass-secondary">
                        {eventTime}
                      </span>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Lieu */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="MapPin" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-location-text">
                  <span className="event-card__glass-location-name">
                    {formData.locationName || formData.location || 'Location not set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Prix */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="Ticket" size={20} />
              </div>
              <div className="event-card__glass-info event-card__glass-info--row">
                {renderPriceText()}
              </div>
            </div>
          </div>
        </GlassPanel>
      </div>
    </div>
  );

  return (
    <div className="preview-step">
      <div className="preview-step__card-container">
        {hasImage ? renderWithImage() : renderWithoutImage()}
      </div>

      <NotificationBubble
        show={showShareNotification}
        message="Link copied!"
        type="success"
        duration={1500}
        onHide={() => setShowShareNotification(false)}
      />
    </div>
  );
};

export default PreviewStep;
